// SiegerehrungDisplayPage.jsx — Vollbild für zweiten Bildschirm / Beamer
// Gesteuert vom Hauptfenster via BroadcastChannel
import React, { useState, useEffect } from 'react';
import './SiegerehrungDisplayPage.css';

const CHANNEL = 'tda-siegerehrung';

export default function SiegerehrungDisplayPage() {
  const [data, setData]           = useState(null);
  const [animKey, setAnimKey]     = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow hidden
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  // Auto-Vollbild beim Laden (klappt wenn via Popup aus User-Geste geöffnet)
  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fullscreen-Status tracken
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }
  };

  // Chat-Widget und externe Elemente verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT','STYLE','LINK','META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const LS_KEY = 'tda-siegerehrung-state';

    const applyState = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed?.type === 'show') {
          setData({ kategorie: parsed.kategorie, idx: parsed.idx, total: parsed.total });
          setAnimKey(k => k + 1);
        } else {
          setData(null);
        }
      } catch (e) {}
    };

    // 1) Sofort lesen beim Öffnen
    applyState(localStorage.getItem(LS_KEY));

    // 2) storage-Event: feuert in ALLEN anderen Fenstern wenn localStorage sich ändert
    //    (zuverlässiger als BroadcastChannel in Safari)
    const onStorage = (e) => {
      if (e.key === LS_KEY) applyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    // 3) BroadcastChannel als zusätzlicher Kanal (für gleichen Tab)
    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => applyState(e.data);
    } catch (e) {}

    return () => {
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, []);

  // Setup-Overlay: sichtbar solange kein Vollbild aktiv
  if (!isFullscreen) {
    return <SetupOverlay onFullscreen={toggleFullscreen} />;
  }

  return !data
    ? <StandbyScreen onFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />
    : <PodiumDisplay key={animKey} data={data} onFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />;
}

// ── Setup Overlay ─────────────────────────────────────────────────────
function SetupOverlay({ onFullscreen }) {
  const [copied, setCopied] = useState(false);
  const displayUrl = `${window.location.origin}/siegerehrung-display`;

  const copyUrl = () => {
    navigator.clipboard.writeText(displayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="sdp-setup">
      <div className="sdp-setup-kanji">表彰</div>
      <div className="sdp-setup-card">
        <div className="sdp-setup-title">Zweiter Bildschirm einrichten</div>

        {/* Option A: Fenster verschieben */}
        <div className="sdp-setup-option">
          <div className="sdp-setup-option-label">Option A — Fenster verschieben</div>
          <div className="sdp-setup-drag">
            <div className="sdp-drag-arrow">←</div>
            <div className="sdp-drag-text">
              Fenster an den <strong>Bildschirmrand ziehen</strong> — macOS schiebt es auf den anderen Monitor
            </div>
          </div>
          <button className="sdp-setup-btn" onClick={onFullscreen}>
            ⛶ Vollbild starten
          </button>
        </div>

        <div className="sdp-setup-divider">oder</div>

        {/* Option B: URL direkt öffnen */}
        <div className="sdp-setup-option">
          <div className="sdp-setup-option-label">Option B — URL direkt öffnen</div>
          <p className="sdp-setup-hint">Im Browser des externen Monitors diese URL eingeben:</p>
          <div className="sdp-setup-url">{displayUrl}</div>
          <button className="sdp-setup-copy" onClick={copyUrl}>
            {copied ? '✓ Kopiert!' : '📋 URL kopieren'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Standby ───────────────────────────────────────────────────────────
function StandbyScreen({ onFullscreen, isFullscreen }) {
  return (
    <div className="sdp-standby" onClick={onFullscreen} title="Klicken für Vollbild">
      <div className="sdp-standby-kanji">武</div>
      <div className="sdp-standby-brand">
        <span className="sdp-brand-tda">TDA</span>
        <span className="sdp-brand-sep"> · </span>
        <span className="sdp-brand-systems">SYSTEMS</span>
      </div>
      <div className="sdp-standby-sub">Tiger &amp; Dragon Association International</div>
      <div className="sdp-standby-dot" />
      {!isFullscreen && (
        <div className="sdp-fullscreen-hint">⛶ Klicken für Vollbild</div>
      )}
    </div>
  );
}

const SDP_CORNERS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

// ── Podium Display ────────────────────────────────────────────────────
function PodiumDisplay({ data, onFullscreen, isFullscreen }) {
  const { kategorie, idx, total } = data;
  const platz1 = kategorie.plaetze.find(p => p.platzierung === 1);
  const platz2 = kategorie.plaetze.find(p => p.platzierung === 2);
  const platz3 = kategorie.plaetze.find(p => p.platzierung === 3);
  const weiteres = kategorie.plaetze
    .filter(p => p.platzierung > 3)
    .sort((a, b) => a.platzierung - b.platzierung);

  const renderSpot = (platz, medal, medalLabel, cls, delay, rank) => (
    <div className={`sdp-spot ${cls} ${!platz ? 'empty' : ''}`} style={{ '--delay': delay }}>
      <div className="sdp-spot-inner">
        <div className="sdp-medal-wrap">
          <span className="sdp-medal">{medal}</span>
        </div>
        <div className="sdp-rank">{rank}</div>
        {platz ? (
          <>
            <div className="sdp-name">
              <span className="sdp-vorname">{platz.vorname}</span>
              <span className="sdp-nachname">{platz.nachname}</span>
            </div>
            <div className="sdp-dojo">{platz.verein_name}</div>
            {platz.punkte != null && (
              <div className="sdp-pts">{parseFloat(platz.punkte).toFixed(2)} Pkt</div>
            )}
          </>
        ) : (
          <div className="sdp-empty-slot">{medalLabel}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="sdp-page">

      {/* Decorative background kanji */}
      <div className="sdp-bg-kanji" aria-hidden>武</div>

      {/* Corner decorations */}
      {SDP_CORNERS.map(pos => (
        <div key={pos} className={`sdp-corner ${pos}`}>
          <div className="sdp-corner-h" />
          <div className="sdp-corner-v" />
          <div className="sdp-corner-dot" />
        </div>
      ))}

      {/* Counter (subtle top-right info) */}
      <div className="sdp-counter-bar">{idx + 1} / {total}</div>

      {/* Prestige header */}
      <div className="sdp-prestige-header">
        <span className="sdp-kanji-main">表彰</span>
        <span className="sdp-siegerehrung-title">S I E G E R E H R U N G</span>
        <h1 className="sdp-kat-name">{kategorie.kategorie_name}</h1>
        {(kategorie.division_code || kategorie.division_type) && (
          <p className="sdp-kat-sub">
            {[kategorie.division_code, kategorie.division_type].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>

      {/* Podium */}
      <div className="sdp-podium-wrap">
        <div className="sdp-podium">
          {renderSpot(platz2, '🥈', '2. Platz', 'spot-2', '0.2s', 2)}
          {renderSpot(platz1, '🥇', '1. Platz', 'spot-1', '0.05s', 1)}
          {renderSpot(platz3, '🥉', '3. Platz', 'spot-3', '0.35s', 3)}
        </div>

        {/* Bases */}
        <div className="sdp-bases">
          <div className="sdp-base base-2"><span>2</span></div>
          <div className="sdp-base base-1"><span>1</span></div>
          <div className="sdp-base base-3"><span>3</span></div>
        </div>
      </div>

      {/* Further places */}
      {weiteres.length > 0 && (
        <div className="sdp-weiteres">
          <div className="sdp-w-label">W E I T E R E &nbsp; P L A T Z I E R U N G E N</div>
          <div className="sdp-w-list">
            {weiteres.map((p, i) => (
              <div key={i} className="sdp-w-entry">
                <span className="sdp-w-platz">{p.platzierung}.</span>
                <span className="sdp-w-name">{p.vorname} {p.nachname}</span>
                <span className="sdp-w-club">{p.verein_name}</span>
                {p.punkte != null && (
                  <span className="sdp-w-pts">{parseFloat(p.punkte).toFixed(2)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="sdp-footer" onClick={onFullscreen} style={{ cursor: 'pointer' }}>
        {isFullscreen ? '⛶ Vollbild beenden' : '⛶ Vollbild starten'}
      </div>

    </div>
  );
}
